import { h, FunctionComponent } from 'preact';
import { useCallback, useEffect, useState } from 'preact/hooks';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import * as styles from '~src/styles/domains/faq/FaqPage.module.scss';
import { trackClick } from '~src/utils/tracking';
import { Page } from '../common/enums/api';
import { general } from '../common/state/commonReducer';
import {
  brandSelector,
  localeSelector,
  previousPageSelector,
} from '../common/state/commonSelectors';
import { Brand } from '../common/enums/common';
import {
  AccordionDetails,
  AccordionSummary,
  AccordionWrapper,
  Icon,
  LanguageToggle,
} from 'up-ops-web-shared-components';
import { ChangeEvent } from 'react';
import i18n from '~src/i18n';
import { changeLocale } from '~src/utils/helpers';
import { Country, Language, Locale } from '../common/enums/localization';

type FAQType = {
  id: number;
  title: string;
  body: string;
  doNotUseTrans?: boolean;
};
type InfoFAQMode = 'faq-general' | 'faq-payments';

const FAQPage: FunctionComponent = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  let brand = useSelector(brandSelector);
  const locale = useSelector(localeSelector);
  const previousPage = useSelector(previousPageSelector);
  const [hasLoaded, setHasLoaded] = useState<boolean>(false);
  const openPaymentFaq = previousPage === Page.ACCEPT && !hasLoaded;
  const [, country] = locale.split('-');
  // translation variables
  const productName = t(`common:${brand.toLowerCase()}.productName`);
  const companyName = t(`common:${brand.toLowerCase()}.companyName`);
  const paymentWebsite = t(`common:${brand.toLowerCase()}.website`);
  const fullAddress = t(`common:${brand.toLowerCase()}.fullAddress`);
  const productMessage =
    brand === Brand.UPLIFT
      ? t('common:uplift.productName')
      : t('common:flexpay.productMessage', { productName });
  const payableCompanyName = t(
    `common:${brand.toLowerCase()}.payableCompanyName`,
  );
  const payableCompanyAddressName = t(
    `common:${brand.toLowerCase()}.payableCompanyAddressName`,
  );
  const allowedLangsMap = {
    [Language.EN]: 'English',
    [Language.FR]: 'Français',
  };
  const [activeTabKey, setActiveTabKey] = useState<InfoFAQMode>(
    openPaymentFaq ? 'faq-payments' : 'faq-general',
  );
  const faqList = t('faq:faq.faqListGeneral', {
    returnObjects: true,
    productName,
    companyName,
    productMessage,
  }) as [FAQType];
  let faqListGeneral = t('faq:faq.faqListGeneral', {
    returnObjects: true,
    productName,
    companyName,
    productMessage,
  }) as [FAQType];
  let faqListPayments = t('faq:faq.faqListPayments', {
    returnObjects: true,
    productName,
    companyName,
    paymentWebsite,
    payableCompanyName,
    payableCompanyAddressName,
    fullAddress,
  }) as [FAQType];

  let statuses: { [key: string]: boolean } = {};

  const buildStatuses = (faqList: [FAQType], faqId?: number) => {
    const initialId = faqId || 0;

    for (let i = 0; i < faqList.length; i++) {
      statuses = {
        ...statuses,
        [faqList[i].id]: faqList[i].id === initialId,
      };
    }
    return statuses;
  };
  const [curFaqList, setCurFaqList] = useState(faqList);
  const [faqOpenStatus, setFaqOpenStatus] = useState<any>(null);

  const setInfoModalFaq = useCallback(
    (mode: InfoFAQMode) => {
      const payableCompanyAddressName = t(
        `common:${brand.toLowerCase()}.payableCompanyAddressName`,
      );

      faqListGeneral = t('faq:faq.faqListGeneral', {
        returnObjects: true,
        productName,
        companyName,
        productMessage,
      }) as [FAQType];
      faqListPayments = t('faq:faq.faqListPayments', {
        returnObjects: true,
        productName,
        companyName,
        paymentWebsite,
        payableCompanyName,
        payableCompanyAddressName,
        fullAddress,
      }) as [FAQType];
      if (mode === 'faq-general') {
        setCurFaqList(faqListGeneral);
        setFaqOpenStatus(buildStatuses(faqListGeneral));
      } else {
        setCurFaqList(faqListPayments);

        if (openPaymentFaq) {
          setFaqOpenStatus(buildStatuses(faqListPayments, 3));
        } else {
          setFaqOpenStatus(buildStatuses(faqListPayments));
        }
      }
    },
    [locale],
  );

  const onTabClick = (tabKey: InfoFAQMode) => {
    setActiveTabKey(tabKey);
    setInfoModalFaq(tabKey);
    setFaqOpenStatus(buildStatuses(curFaqList));
  };

  useEffect(() => {
    setInfoModalFaq('faq-general');
    setInfoModalFaq('faq-payments');
    setFaqOpenStatus(buildStatuses(curFaqList));

    return () => {};
  }, []);

  useEffect(() => {
    setInfoModalFaq('faq-general');
    setInfoModalFaq('faq-payments');
    setFaqOpenStatus(buildStatuses(curFaqList));
    setHasLoaded(true);
  }, [locale]);

  const onChange =
    (id: string) => (event: ChangeEvent<{}>, expanded: boolean) => {
      if (expanded) {
        if (event.type === 'click') {
          dispatch(trackClick(`faq-dropdown-${id}`));
        }

        let newStatus = {};
        curFaqList.forEach(faq => {
          if (faq.id !== +id) {
            newStatus = { ...newStatus, [faq.id]: false };
          } else {
            newStatus = { ...newStatus, [faq.id]: true };
          }
        });
        setFaqOpenStatus(newStatus);
      } else {
        setFaqOpenStatus({ ...faqOpenStatus, [id]: false });
      }
    };

  const onSelect = (language: string) => {
    dispatch(
      changeLocale(`${language.toLowerCase()}-${country}` as Locale, i18n),
    );
  };

  useEffect(() => {
    setInfoModalFaq(activeTabKey);
  }, [locale]);

  const closeHandler = () => {
    dispatch(general({ page: previousPage, previousPage: null }));
  };

  return (
    <div className={styles.faq} role='presentation'>
      <div className={styles['header-buttons']}>
        <Icon name='chevron_left' size='30px' color='inherit' />
        <button
          className={styles['faq-back']}
          type='button'
          id='faq-back'
          aria-label='Close this dialog window'
          onClick={() => closeHandler()}
          onKeyDown={e => e.key === 'Enter' && closeHandler()}
        >
          {t('faq:buttons.back.label')}
        </button>
      </div>
      <div className={styles['header-txt']}>
        <h1>{t('faq:faq.header')}</h1>
        <div className={styles['faq-tabs']}>
          <button
            id='faq-general'
            className={`${styles['faq-tab']} ${
              activeTabKey === 'faq-general' && styles['faq-tab-active']
            }`}
            type='button'
            onClick={() => onTabClick('faq-general')}
          >
            {t('faq:faq.general').toUpperCase()}
          </button>
          <button
            id='faq-payments'
            className={`${styles['faq-tab']} ${
              activeTabKey === 'faq-payments' && styles['faq-tab-active']
            }`}
            type='button'
            onClick={() => onTabClick('faq-payments')}
          >
            {t('faq:faq.payments').toUpperCase()}
          </button>
        </div>
      </div>
      <div className={styles['faq-body']} id='faq-accordion-container'>
        {curFaqList.map((faq, index) => (
          <AccordionWrapper
            id={String(index)}
            expanded={faqOpenStatus && faqOpenStatus[faq.id]}
            className={styles['faq-accordian-details']}
            onChange={onChange(String(faq.id))}
          >
            <AccordionSummary className={styles['faq-title']}>
              {faq.title}
            </AccordionSummary>
            <AccordionDetails>
              {faq.doNotUseTrans ? faq.body : <Trans>{faq.body}</Trans>}
            </AccordionDetails>
          </AccordionWrapper>
        ))}
      </div>
      {country === Country.CA && (
        <div className={styles['language-toggle-wrapper']}>
          <LanguageToggle
            className={styles['language-toggle']}
            languages={allowedLangsMap}
            onSelect={onSelect}
            displayLocale={false}
          />
        </div>
      )}
    </div>
  );
};

export default FAQPage;
